import type { ContactSummaryDto } from '@sharedlib/dtos/dto/contacts-app/contact.dto';
import type { GroupSummaryDto } from '@sharedlib/dtos/dto/contacts-app/group.dto';
import { AccountService } from '@frontendlib/common/services/account.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { SpacesService } from '@modules/settings/services/spaces.service';
import { type Space } from '@modules/settings/types/space';
import { Team } from '@modules/settings/types/team';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { catchError, map, of, switchMap, take, takeUntil, tap, timeout } from 'rxjs';
import { workspaceColor, workspacePalette } from '@modules/common/utils/workspace-color';
import { ContactsTenantScopeService } from '../../services/contacts-tenant-scope.service';
import { type SidebarItemComponent } from '@modules/common/components/sidebar-item/sidebar-item.component';
import { Component, inject, Injector, signal } from '@angular/core';
import { Application } from '@modules/common/types/application';
import { SidebarItem } from '@modules/common/types/sidebar-item';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { ContactsService } from '@modules/contacts/services/contacts.service';
import { GroupsService } from '@modules/contacts/services/groups.service';
import { Contact } from '@modules/contacts/types/contact';
import { Group } from '@modules/contacts/types/group';
import { moveItemsIntoContainer } from '@modules/common/utils/move-into-container.util';
import { VirtualFolder } from '@modules/contacts/types/virtual-folder';
import { DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { contactsPinnedMenu } from '../sidebar-context-menu/contacts-rail-menu';

// Components
import { BaseSidebarComponent } from '@modules/common/components/base-sidebar/base-sidebar.component';

@Component({
  selector: 'app-contacts-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.less'],
  standalone: false,
})
export class ContactsSideBarComponent extends BaseSidebarComponent<Group, Contact, VirtualFolder> {
  application = Application.contacts;
  sidebarSplitViewKey = SidebarSplitViewKey.contacts;
  selfDragDataTypes: DragDataTypes[] = [DragDataTypes.contact, DragDataTypes.group];

  // Make enum accessible in template
  public readonly SidebarSplitViewKey = SidebarSplitViewKey;

  public baseItems: SidebarItem[] = [
    { title: 'Contacts', id: 'all_contacts', icon: 'sapphire-contact' },
    { title: 'VIP', id: 'vip', icon: 'sapphire-vip' },
    { title: 'Flagged', id: 'flagged', icon: 'sapphire-flagged' },
    { title: 'Follow Up', id: 'followed', icon: 'sapphire-follow-up' },
    { title: 'Snoozed', id: 'snoozed', icon: 'sapphire-snooze' },
    { title: 'Archive', id: 'archived', icon: 'sapphire-archive' },
    { title: 'Trash', id: 'deleted', icon: 'sapphire-trash' },
  ];

  public readonly primaryItems = this.baseItems.slice(0, 3);
  public readonly moreItems = this.baseItems.slice(3);
  /** My Workspace's primary rows: the prototype names the member's first row "My Contacts" (P/app.js:99). */
  public readonly myPrimaryItems: SidebarItem[] = [
    { ...this.primaryItems[0], title: 'My Contacts' },
    ...this.primaryItems.slice(1),
  ];
  /** The Pinned / Team Knots and Tags rows' menus: the prototype's entry, then Contacts' own Add and Manage. */
  protected readonly pinnedMenus = {
    mine: { knots: contactsPinnedMenu('knots', 'mine'), tags: contactsPinnedMenu('tags', 'mine') },
    team: { knots: contactsPinnedMenu('knots', 'team'), tags: contactsPinnedMenu('tags', 'team') },
  } as const;
  protected readonly workspaceResolved = signal(false);
  protected readonly myWorkspaceExpanded = signal(true);
  protected readonly teamWorkspaceExpanded = signal(true);
  protected readonly currentTeamWorkspaceExpanded = signal(true);
  protected readonly moreExpanded = signal(false);
  protected readonly teamMoreExpanded = signal(false);
  protected readonly workspaceColor = workspaceColor;
  protected readonly workspacePalette = workspacePalette;
  protected currentTeamWorkspace: Space | undefined;
  protected currentMemberTenant: Team | undefined;
  protected currentTeamTenant: Team | undefined;
  protected currentTeamWorkspaceInitials = '';
  private readonly context = inject(SpaceContextStore);
  private readonly accountService = inject(AccountService);
  private readonly spacesService = inject(SpacesService);
  private readonly contactsScope = inject(ContactsTenantScopeService);
  private dropTenant: string | null | undefined;

  public override ngOnInit(): void {
    super.ngOnInit();
    this.context.contextId$
      .pipe(
        tap(() => this.workspaceResolved.set(false)),
        switchMap((contextId) =>
          this.context.type() === SpaceType.PERSONAL
            ? of({ items: [] as Space[], contextId })
            : this.accountService.getAccount(true).pipe(
                switchMap((account) =>
                  this.spacesService.listWithInvites({ archived: false, limit: 100 }, { account }),
                ),
                take(1),
                // Bound the whole account→spaces lookup before the scope's ten-second last-resort gate.
                timeout({ first: 8_000 }),
                map(({ items }) => ({ items, contextId })),
                catchError(() => of(null)),
              ),
        ),
        takeUntil(this.alive),
      )
      .subscribe((result) => {
        if (!result) {
          this.currentTeamWorkspace = undefined;
          this.currentMemberTenant = undefined;
          this.currentTeamTenant = undefined;
          this.contactsScope.degradeToPersonal();
        } else {
          this.currentTeamWorkspace = result.items.find(
            ({ currentUserStatus, id }) => currentUserStatus === 'accepted' && id === result.contextId,
          );
          this.currentMemberTenant = this.currentTeamWorkspace?.myMailboxTenantId
            ? new Team({ id: this.currentTeamWorkspace.myMailboxTenantId })
            : undefined;
          this.currentTeamTenant = this.currentTeamWorkspace
            ? new Team({ id: this.currentTeamWorkspace.id })
            : undefined;
          if (this.currentTeamWorkspace && !this.currentMemberTenant) {
            this.contactsScope.degradeToPersonal();
            this.currentTeamWorkspace = undefined;
            this.currentTeamTenant = undefined;
          } else {
            this.contactsScope.resolveInitialTenant(this.currentMemberTenant?.id ?? null);
          }
          this.currentTeamWorkspaceInitials =
            this.currentTeamWorkspace?.title
              .trim()
              .split(/\s+/)
              .slice(0, 2)
              .map((word) => word[0])
              .join('')
              .toUpperCase() ?? '';
        }
        this.workspaceResolved.set(true);
      });
  }

  constructor(
    injector: Injector,
    private contactsService: ContactsService,
    private groupsService: GroupsService,
  ) {
    super(injector);
  }

  /**
   * Actions
   */

  public override selectFilter(folder: VirtualFolder, tenant?: Team): void {
    const reselected = this.isActiveRow(folder, tenant);
    this.stateService.setTeam(tenant);
    this.stateService.setVirtualFolder(folder);
    if (reselected) {
      this.stateService.reselectVirtualFolder();
    }
  }

  protected dndDropOnRow(data: DragData, folder: VirtualFolder, row: SidebarItemComponent, tenant?: Team): void {
    if (!this.selfDragDataTypes.includes(data.type)) {
      this.dropTenant = tenant?.id ?? null;
      this.contactsScope.selectTenant(this.dropTenant);
    }
    try {
      this.dndDrop(data, folder, this.elements.toArray().indexOf(row));
    } finally {
      this.dropTenant = undefined;
    }
  }

  handleMove(dragData: DragData, sidebarFolder: VirtualFolder): void {
    const restoreOptions = { skipArchiveRestore: sidebarFolder === 'archived' };
    if (dragData.type === DragDataTypes.contact) {
      for (const items of this.ownerBatches(dragData.data as ContactSummaryDto[])) {
        const ids = this.getIds(items);
        const config = items[0].accountId ? { tenantItems: items } : {};
        if (sidebarFolder === 'deleted') {
          this.contactsService.delete({ ids }, true, undefined, config);
          continue;
        }
        const { changes, message } = Contact.getChangesForVirtualFolder(sidebarFolder);
        moveItemsIntoContainer(
          this.contactsService,
          items,
          () => this.contactsService.bunchUpdate({ ids }, changes, { ...config, message }),
          restoreOptions,
        );
      }
    } else if (dragData.type === DragDataTypes.group) {
      for (const items of this.ownerBatches(dragData.data as GroupSummaryDto[])) {
        const ids = this.getIds(items);
        const config = items[0].accountId ? { tenantItems: items } : {};
        if (sidebarFolder === 'deleted') {
          this.groupsService.delete({ ids }, true, undefined, config);
          continue;
        }
        const { changes, message } = Group.getChangesForVirtualFolder(sidebarFolder);
        moveItemsIntoContainer(
          this.groupsService,
          items,
          () => this.groupsService.bunchUpdate({ ids }, changes, { ...config, message }),
          restoreOptions,
        );
      }
    }
  }

  private ownerBatches<T extends { readonly accountId?: string }>(items: readonly T[]): T[][] {
    const batches = new Map<string | undefined, T[]>();
    for (const item of items) {
      const batch = batches.get(item.accountId) ?? [];
      batch.push(item);
      batches.set(item.accountId, batch);
    }
    return [...batches.values()];
  }

  handleMore(item: Contact) {
    this.stateService.setMainView(item);
  }

  getStitchFromDragData(dragData: DragData, sidebarFolder: VirtualFolder): Contact {
    const { changes } = Contact.getChangesForVirtualFolder(sidebarFolder);

    return new Contact({
      ...changes,
      accountId: this.dropTenant === undefined ? this.contactsScope.effectiveTenantId() : this.dropTenant,
      title: dragData.data[0]['title'],
    });
  }
}
